import { useEffect, useRef } from "react";
import "@/App.css";
import { ArrowRight, Check, Car, Monitor, Sofa, Shield, Grip, Pocket, Sparkles } from "lucide-react";

// Product image URL
const PRODUCT_IMAGE = "https://customer-assets.emergentagent.com/job_comfort-engineered/artifacts/eij6ei85_img-Photoroom.png";

// Use case images
const USE_IMAGES = {
  office: "https://images.unsplash.com/photo-1758876021212-a87517fc8954?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxvZmZpY2UlMjB3b3JrZXIlMjBtb2Rlcm4lMjBkZXNrfGVufDB8fHx8MTc3NDExMDkzM3ww&ixlib=rb-4.1.0&q=85",
  driving: "https://images.unsplash.com/photo-1769032292447-38a846e81ef8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxkcml2aW5nJTIwY2FyJTIwaW50ZXJpb3IlMjBzdGVlcmluZyUyMHdoZWVsfGVufDB8fHx8MTc3NDExMDkzNHww&ixlib=rb-4.1.0&q=85",
  home: "https://images.unsplash.com/photo-1759722665610-e13e59aa117b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwc29mYSUyMGNvenl8ZW58MHx8fHwxNzc0MTEwOTM1fDA&ixlib=rb-4.1.0&q=85",
};

// Intersection Observer hook for scroll reveal
const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const revealElements = document.querySelectorAll(".reveal");
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-cream-light/80 backdrop-blur-md border-b border-black/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#" data-testid="header-logo" className="font-serif text-2xl font-semibold text-[#1A1A1A] tracking-tight">
          MyMedisure
        </a>
        <a
          href="#"
          data-testid="header-buy-button"
          className="btn-primary bg-teal text-cream-light px-6 py-2.5 rounded-full font-sans font-medium text-sm flex items-center gap-2"
        >
          Buy on Amazon
          <ArrowRight className="w-4 h-4 arrow-animate" />
        </a>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="noise-overlay hero-gradient min-h-screen pt-24 pb-16 md:pb-0 md:pt-0 flex items-center" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 w-full">
        <div className="md:grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className="reveal">
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light text-[#1A1A1A] leading-[1.1] mb-6">
              Your Back<br />
              <span className="font-semibold">Deserves Better.</span>
            </h1>
            <p className="font-sans text-lg md:text-xl text-[#5C5C5C] mb-8 max-w-lg leading-relaxed">
              Premium memory foam lumbar support, engineered for all-day comfort.
            </p>
            <a
              href="#"
              data-testid="hero-buy-button"
              className="btn-primary inline-flex items-center gap-3 bg-teal text-cream-light px-8 py-4 rounded-full font-sans font-medium text-base"
            >
              Buy on Amazon
              <ArrowRight className="w-5 h-5 arrow-animate" />
            </a>
          </div>

          {/* Right: Product Image */}
          <div className="reveal reveal-delay-1 mt-12 md:mt-0 flex justify-center md:justify-end">
            <div className="relative">
              <img
                src={PRODUCT_IMAGE}
                alt="MyMedisure Premium Back Support Cushion"
                className="w-full max-w-md md:max-w-lg drop-shadow-2xl"
                data-testid="hero-product-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why MyMedisure Section
const WhySection = () => {
  const benefits = [
    {
      title: "High-Density Memory Foam Core",
      description: "Contours perfectly to your spine, providing personalized support that lasts all day without flattening.",
      icon: <Sparkles className="w-6 h-6" />,
    },
    {
      title: "Breathable 3D Mesh Cover",
      description: "Advanced ventilation technology keeps you cool and comfortable, even during long hours of sitting.",
      icon: <Shield className="w-6 h-6" />,
    },
    {
      title: "Universal Fit Design",
      description: "Fits seamlessly on office chairs, car seats, sofas, and wheelchairs. Take comfort everywhere.",
      icon: <Check className="w-6 h-6" />,
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-cream-light" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl font-light text-[#1A1A1A] mb-16 text-center">
          Why <span className="font-semibold">MyMedisure</span>?
        </h2>

        <div className="grid md:grid-cols-3 gap-12">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`reveal reveal-delay-${index + 1} benefit-card`}
              data-testid={`benefit-card-${index}`}
            >
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center text-teal mb-6">
                {benefit.icon}
              </div>
              <h3 className="font-serif text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4">
                {benefit.title}
              </h3>
              <p className="font-sans text-[#5C5C5C] leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pain Relief Zones Section
const PainReliefSection = () => {
  const zones = [
    { label: "Upper Back Relief", position: "top-[15%]" },
    { label: "Mid Back Support", position: "top-[45%]" },
    { label: "Lower Lumbar Care", position: "top-[75%]" },
  ];

  return (
    <section className="py-24 md:py-32 bg-cream" data-testid="pain-relief-section">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl font-light text-[#1A1A1A] mb-6 text-center">
          Comfort Where It <span className="font-semibold">Counts</span>
        </h2>
        <p className="reveal font-sans text-[#5C5C5C] text-center max-w-2xl mx-auto mb-16">
          Engineered to target the three critical zones of your back for complete support.
        </p>

        <div className="reveal flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
          {/* Left Labels */}
          <div className="hidden md:flex flex-col gap-16 text-right">
            <div className="reveal reveal-delay-1" data-testid="pain-zone-upper">
              <h4 className="font-serif text-lg font-semibold text-[#1A1A1A]">Upper Back Relief</h4>
              <p className="font-sans text-sm text-[#5C5C5C]">Releases tension from shoulders</p>
            </div>
            <div className="reveal reveal-delay-2" data-testid="pain-zone-mid">
              <h4 className="font-serif text-lg font-semibold text-[#1A1A1A]">Mid Back Support</h4>
              <p className="font-sans text-sm text-[#5C5C5C]">Aligns thoracic vertebrae</p>
            </div>
          </div>

          {/* Center: Product with dots */}
          <div className="relative w-64 md:w-80">
            <img
              src={PRODUCT_IMAGE}
              alt="Pain relief zones visualization"
              className="w-full"
              data-testid="pain-relief-product-image"
            />
            {/* Pain point indicators */}
            <div className="absolute top-[18%] right-[30%] pulse-dot w-3 h-3 bg-teal rounded-full" />
            <div className="absolute top-[45%] right-[25%] pulse-dot w-3 h-3 bg-teal rounded-full" />
            <div className="absolute top-[72%] right-[30%] pulse-dot w-3 h-3 bg-teal rounded-full" />
          </div>

          {/* Right Label */}
          <div className="hidden md:block text-left">
            <div className="reveal reveal-delay-3" data-testid="pain-zone-lower">
              <h4 className="font-serif text-lg font-semibold text-[#1A1A1A]">Lower Lumbar Care</h4>
              <p className="font-sans text-sm text-[#5C5C5C]">Supports natural curve</p>
            </div>
          </div>

          {/* Mobile Labels */}
          <div className="flex md:hidden flex-col gap-4 text-center mt-8">
            {zones.map((zone, index) => (
              <div key={index} className="reveal" data-testid={`pain-zone-mobile-${index}`}>
                <h4 className="font-serif text-lg font-semibold text-[#1A1A1A]">{zone.label}</h4>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Key Features Section
const FeaturesSection = () => {
  const features = [
    {
      title: "Anti-Slip Back Design",
      description: "Stays securely in place on any chair surface.",
      icon: <Grip className="w-6 h-6" />,
    },
    {
      title: "Adjustable Elastic Strap",
      description: "Universal fit for chairs of all sizes.",
      icon: <Shield className="w-6 h-6" />,
    },
    {
      title: "Smart Side Utility Pocket",
      description: "Keep your phone and essentials within reach.",
      icon: <Pocket className="w-6 h-6" />,
    },
    {
      title: "Washable Removable Cover",
      description: "Easy to clean, always fresh and hygienic.",
      icon: <Sparkles className="w-6 h-6" />,
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-cream-light" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl font-light text-[#1A1A1A] mb-16 text-center">
          Thoughtfully <span className="font-semibold">Designed</span>
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`reveal reveal-delay-${(index % 3) + 1} feature-card`}
              data-testid={`feature-card-${index}`}
            >
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center text-teal mb-6">
                {feature.icon}
              </div>
              <h3 className="font-serif text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-3">
                {feature.title}
              </h3>
              <p className="font-sans text-[#5C5C5C]">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Use Anywhere Section
const UseAnywhereSection = () => {
  const useCases = [
    {
      title: "Working",
      description: "Transform your office chair into an ergonomic haven.",
      image: USE_IMAGES.office,
      icon: <Monitor className="w-5 h-5" />,
    },
    {
      title: "Driving",
      description: "Long commutes made comfortable.",
      image: USE_IMAGES.driving,
      icon: <Car className="w-5 h-5" />,
    },
    {
      title: "Relaxing",
      description: "Movie nights with proper support.",
      image: USE_IMAGES.home,
      icon: <Sofa className="w-5 h-5" />,
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-cream" data-testid="use-anywhere-section">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl font-light text-[#1A1A1A] mb-6 text-center">
          Use <span className="font-semibold">Anywhere</span>
        </h2>
        <p className="reveal font-sans text-[#5C5C5C] text-center max-w-2xl mx-auto mb-16">
          One cushion, endless possibilities. Take your comfort wherever life takes you.
        </p>

        <div className="grid md:grid-cols-3 gap-8">
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className={`reveal reveal-delay-${index + 1}`}
              data-testid={`use-case-${index}`}
            >
              <div className="use-card image-zoom rounded-2xl overflow-hidden mb-6">
                <img
                  src={useCase.image}
                  alt={useCase.title}
                  className="w-full h-80 md:h-96 object-cover"
                />
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="text-teal">{useCase.icon}</div>
                <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">
                  {useCase.title}
                </h3>
              </div>
              <p className="font-sans text-[#5C5C5C] text-sm">
                {useCase.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Banner Section
const CTABanner = () => {
  return (
    <section className="py-16 md:py-24 bg-cream-light" data-testid="cta-banner-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="reveal cta-banner py-16 md:py-20 px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-cream-light mb-6 leading-tight">
            Start Your Journey to a<br />
            <span className="font-semibold">Pain-Free Day</span>
          </h2>
          <p className="font-sans text-cream-light/80 mb-8 max-w-lg mx-auto">
            Join thousands who have transformed their sitting experience with MyMedisure.
          </p>
          <a
            href="#"
            data-testid="cta-banner-button"
            className="inline-flex items-center gap-3 bg-cream-light text-teal px-8 py-4 rounded-full font-sans font-medium text-base transition-transform hover:scale-105"
          >
            Shop Now on Amazon
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  return (
    <footer className="py-12 bg-cream border-t border-black/5" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <a href="#" className="font-serif text-2xl font-semibold text-[#1A1A1A] mb-4 inline-block" data-testid="footer-logo">
          MyMedisure
        </a>
        <p className="font-sans text-[#5C5C5C] text-sm mb-6">
          Feel the Difference.
        </p>
        <p className="font-sans text-[#5C5C5C]/60 text-xs">
          &copy; {new Date().getFullYear()} MyMedisure. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  useScrollReveal();

  return (
    <div className="App bg-cream-light min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <WhySection />
        <PainReliefSection />
        <FeaturesSection />
        <UseAnywhereSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
}

export default App;
